import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { RadioGroup, RadioGroupItem } from "../components/ui/radio-group";
import { Building2, Mail, Lock, User, Phone, AlertCircle, Truck, UserCheck, Shield } from "lucide-react";
import { toast } from "sonner";

const Register = () => {
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    confirmPassword: "",
    full_name: "",
    phone: "",
    role: "employee",
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const { register } = useAuth();
  const navigate = useNavigate();

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (formData.password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }

    setIsLoading(true);

    try {
      const { confirmPassword, ...userData } = formData;
      const result = await register(userData);
      if (result.kind === "setup") {
        toast.success("Account created! Now set up two-factor authentication.");
        navigate("/mfa-setup");
        return;
      }
    } catch (err) {
      const message = err.response?.data?.detail || "Registration failed";
      setError(typeof message === "string" ? message : "Registration failed");
      toast.error(typeof message === "string" ? message : "Registration failed");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-stone-50">
      {/* Left Side - Image */}
      <div className="hidden lg:flex flex-1 relative">
        <div className="absolute inset-0 bg-emerald-900/90"></div>
        <img
          src="https://images.unsplash.com/photo-1764727291644-5dcb0b1a0375?crop=entropy&cs=srgb&fm=jpg&q=85"
          alt="Modern medical clinic reception"
          className="object-cover w-full h-full opacity-40"
        />
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="text-white text-center max-w-lg">
            <h2 className="text-3xl font-bold mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Join Comprehensive Pain Consultants Today
            </h2>
            <p className="text-emerald-100">
              Whether you're a vendor or an employee, get paid quickly and securely with bank-level protection.
            </p>
          </div>
        </div>
      </div>

      {/* Right Side - Form */}
      <div className="flex-1 flex items-center justify-center p-8 overflow-y-auto">
        <div className="w-full max-w-md py-8">
          <Link to="/" className="flex items-center mb-8">
            <img src="/cpc-logo.png" alt="Comprehensive Pain Consultants" className="h-10 w-auto rounded-lg bg-emerald-900 px-3 py-2" />
          </Link>

          <Card className="border-stone-200 shadow-lg" data-testid="register-card">
            <CardHeader className="space-y-1">
              <CardTitle className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                Create an account
              </CardTitle>
              <CardDescription>
                Get started with Comprehensive Pain Consultants in minutes
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                {error && (
                  <div className="flex items-center gap-2 p-3 bg-red-50 text-red-700 rounded-lg text-sm" data-testid="register-error">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" />
                    {error}
                  </div>
                )}

                {/* Role Selection */}
                <div className="space-y-3">
                  <Label>I am a...</Label>
                  <RadioGroup
                    value={formData.role}
                    onValueChange={(value) => setFormData((prev) => ({ ...prev, role: value }))}
                    className="grid grid-cols-3 gap-4"
                  >
                    <div>
                      <RadioGroupItem value="vendor" id="vendor" className="peer sr-only" />
                      <Label
                        htmlFor="vendor"
                        className="flex flex-col items-center justify-between rounded-xl border-2 border-stone-200 bg-white p-4 hover:bg-stone-50 peer-data-[state=checked]:border-emerald-600 peer-data-[state=checked]:bg-emerald-50 cursor-pointer transition-all"
                        data-testid="register-vendor-option"
                      >
                        <Truck className="mb-2 h-6 w-6 text-emerald-700" />
                        <span className="text-sm font-medium">Vendor</span>
                      </Label>
                    </div>
                    <div>
                      <RadioGroupItem value="employee" id="employee" className="peer sr-only" />
                      <Label
                        htmlFor="employee"
                        className="flex flex-col items-center justify-between rounded-xl border-2 border-stone-200 bg-white p-4 hover:bg-stone-50 peer-data-[state=checked]:border-emerald-600 peer-data-[state=checked]:bg-emerald-50 cursor-pointer transition-all"
                        data-testid="register-employee-option"
                      >
                        <UserCheck className="mb-2 h-6 w-6 text-emerald-700" />
                        <span className="text-sm font-medium">Employee</span>
                      </Label>
                    </div>
                    <div>
                      <RadioGroupItem value="account_manager" id="account_manager" className="peer sr-only" />
                      <Label
                        htmlFor="account_manager"
                        className="flex flex-col items-center justify-between rounded-xl border-2 border-stone-200 bg-white p-4 hover:bg-stone-50 peer-data-[state=checked]:border-emerald-600 peer-data-[state=checked]:bg-emerald-50 cursor-pointer transition-all"
                        data-testid="register-manager-option"
                      >
                        <Shield className="mb-2 h-6 w-6 text-emerald-700" />
                        <span className="text-sm font-medium">Manager</span>
                      </Label>
                    </div>
                  </RadioGroup>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="full_name">Full Name</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                    <Input
                      id="full_name"
                      name="full_name"
                      type="text"
                      placeholder="John Doe"
                      value={formData.full_name}
                      onChange={handleChange}
                      className="pl-10 input-field"
                      required
                      data-testid="register-name-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="you@example.com"
                      value={formData.email}
                      onChange={handleChange}
                      className="pl-10 input-field"
                      required
                      data-testid="register-email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone">Phone (optional)</Label>
                  <div className="relative">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder="(555) 123-4567"
                      value={formData.phone}
                      onChange={handleChange}
                      className="pl-10 input-field"
                      data-testid="register-phone-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                    <Input
                      id="password"
                      name="password"
                      type="password"
                      placeholder="••••••••"
                      value={formData.password}
                      onChange={handleChange}
                      className="pl-10 input-field"
                      required
                      data-testid="register-password-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">Confirm Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                    <Input
                      id="confirmPassword"
                      name="confirmPassword"
                      type="password"
                      placeholder="••••••••"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      className="pl-10 input-field"
                      required
                      data-testid="register-confirm-password-input"
                    />
                  </div>
                </div>

                <Button
                  type="submit"
                  className="w-full bg-emerald-900 hover:bg-emerald-800 rounded-full h-11"
                  disabled={isLoading}
                  data-testid="register-submit-btn"
                >
                  {isLoading ? "Creating account..." : "Create Account"}
                </Button>
              </form>

              <div className="mt-6 text-center">
                <p className="text-sm text-stone-600">
                  Already have an account?{" "}
                  <Link to="/login" className="text-emerald-700 hover:text-emerald-800 font-medium">
                    Sign in
                  </Link>
                </p>
                <p className="mt-3 text-xs text-stone-400">
                  By creating an account you agree to our{" "}
                  <Link to="/terms" className="underline hover:text-emerald-700" data-testid="register-terms-link">
                    Terms of Service
                  </Link>
                  ,{" "}
                  <Link to="/privacy-policy" className="underline hover:text-emerald-700" data-testid="register-privacy-link">
                    Privacy Policy
                  </Link>{" "}
                  and{" "}
                  <Link to="/plaid-privacy" className="underline hover:text-emerald-700" data-testid="register-plaid-privacy-link">
                    Plaid Privacy disclosure
                  </Link>
                  .
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default Register;
